import type { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import type { Note, NoteParams, NotePayload, NoteType } from '@/types/note';
import type PaginatedResponse from '@/types/paginatedResponse';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const noteRepository = {
  async getMostRecentNote({
    host,
    noteTypeSlug,
    objectId
  }: {
    host: string;
    noteTypeSlug: string;
    objectId: string;
  }): Promise<RepositoryResponse<Note>> {
    try {
      const path = '/api/notes/most_recent/';
      const response = await ajax.get({
        params: {
          note_type: noteTypeSlug,
          object_id: objectId
        },
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get the most recent note.');
    }
  },

  async getNotes({
    host,
    params
  }: {
    host: string;
    params: NoteParams;
  }): Promise<PaginatedResponse<Note[]>> {
    const path = '/api/notes/';
    const { data } = await ajax.get({
      params: {
        ...params
      },
      url: `${host}${path}`
    });

    return data;
  },

  async getNoteTypes({
    host,
    params
  }: {
    host: string;
    params: { content_type__model: string };
  }): Promise<RepositoryResponse<NoteType[]>> {
    try {
      const path = '/api/notes/note-type/?query';
      const response = await ajax.get({
        url: `${host}${path}`,
        params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get note types.');
    }
  },

  async createNote({ host, note }: { host: string; note: NotePayload }): Promise<Note> {
    const path = '/api/notes/';
    const { data } = await ajax.post<NotePayload, AxiosResponse<Note>>({
      data: note,
      url: `${host}${path}`
    });

    return data;
  },

  async updateNote({ host, note }: { host: string; note: NotePayload }): Promise<Note> {
    const path = `/api/notes/${note.id}/`;
    const { data } = await ajax.patch<NotePayload, AxiosResponse<Note>>({
      data: note,
      url: `${host}${path}`
    });

    return data;
  }
};

const NoteRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getMostRecentNote({
      noteTypeSlug,
      objectId
    }: {
      noteTypeSlug: string;
      objectId: string;
    }): Promise<RepositoryResponse<Note>> {
      return noteRepository.getMostRecentNote({ host, noteTypeSlug, objectId });
    },

    async getNotes({ params }: { params: NoteParams }): Promise<PaginatedResponse<Note[]>> {
      return noteRepository.getNotes({ host, params });
    },

    async getNoteTypes(params: {
      content_type__model: string;
    }): Promise<RepositoryResponse<NoteType[]>> {
      return noteRepository.getNoteTypes({ host, params });
    },

    async createNote({ note }: { note: NotePayload }): Promise<Note> {
      return noteRepository.createNote({ host, note });
    },

    async updateNote({ note }: { note: NotePayload }): Promise<Note> {
      return noteRepository.updateNote({ host, note });
    }
  };
};

export default NoteRepository;
